<template>
    <section class="cliente-page">
        <b-loading :active="isLoading" @update:active="isLoading = $event" />
        <titlebar :title-stack="titleStack" />
        <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
            <form class="cliente-form" @submit.prevent="handleSubmit(onSubmit)">
                <div class="cliente-context-card">
                    <div class="cliente-context-icon">
                        <b-icon :icon="model.id ? 'account-edit-outline' : 'account-plus-outline'" size="is-medium" />
                    </div>
                    <div class="cliente-context-content">
                        <span class="cliente-eyebrow">{{ model.id ? `CLIENTE #${model.id}` : 'NOVO CLIENTE' }}</span>
                        <h1>{{ model.nome || 'Preencha os dados do cliente' }}</h1>
                        <p>
                            {{ model.id ? 'Atualize as informações cadastrais e documentos.' : 'Cadastre os dados de identificação, contato e localização.' }}
                        </p>
                    </div>
                    <div class="required-legend"><span>*</span> Campos obrigatórios</div>
                </div>

                <div class="columns is-variable is-5 cliente-layout">
                    <div class="column is-9-widescreen is-8-desktop" style="padding: 0 !important;">
                        <article class="form-section-card">
                            <header class="form-section-header">
                                <span class="form-section-icon"><b-icon icon="card-account-details-outline" /></span>
                                <div>
                                    <h2>Identificação</h2>
                                    <p>Dados principais usados para identificar o cliente e a empresa.</p>
                                </div>
                            </header>

                            <div class="columns is-multiline">

                                <div class="column is-12">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required" name="Nome do cliente">
                                        <b-field label="Nome do cliente *" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input v-model.trim="model.nome" maxlength="150" @blur="preencherRazaoSocial" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-6">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required" name="Razão social">
                                        <b-field label="Razão social *" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input v-model.trim="model.razaoSocial" maxlength="200" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-6">
                                    <b-field label="Nome fantasia">
                                        <b-input v-model.trim="model.nomeFantasia" maxlength="200" />
                                    </b-field>
                                </div>
                                <div class="column is-6">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required" name="CPF/CNPJ">
                                        <b-field
                                            label="CPF/CNPJ *"
                                            :type="{ 'is-danger': errors[0] || erroCpfCnpj, 'is-success': valid && !erroCpfCnpj }"
                                            :message="erroCpfCnpj || errors"
                                        >
                                            <b-input
                                                v-model="model.cpf"
                                                v-mask="['###.###.###-##', '##.###.###/####-##']"
                                                maxlength="18"
                                                @input="erroCpfCnpj = null"
                                            />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-6">
                                    <b-field label="Insc. Municipal">
                                        <b-input v-model.trim="model.inscricaoMunicipal" maxlength="50" />
                                    </b-field>
                                </div>
                                <div class="column is-6">
                                    <b-field label="Insc. Estadual">
                                        <b-input v-model.trim="model.inscricaoEstadual" maxlength="50" />
                                    </b-field>
                                </div>
                            </div>
                        </article>

                        <article class="form-section-card">
                            <header class="form-section-header">
                                <span class="form-section-icon"><b-icon icon="phone-outline" /></span>
                                <div>
                                    <h2>Contato do cliente</h2>
                                    <p>Canais de contato e e-mail utilizado no relacionamento com o cliente</p>
                                </div>
                            </header>

                            <div class="columns is-multiline">
                                <div class="column is-12">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required" name="Nome do contato do cliente">
                                        <b-field label="Nome *" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input v-model.trim="model.nomeContatoCliente" maxlength="150" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Telefone">
                                        <b-input v-model="model.fone" v-mask="['(##) ####-####', '(##) #####-####']" />
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required" name="Celular">
                                        <b-field label="Celular *" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input
                                                v-model="model.celular"
                                                v-mask="['(##) ####-####', '(##) #####-####']"
                                                icon-right="whatsapp"
                                                :icon-right-clickable="podeEnviarWhatsapp"
                                                :icon-right-type="podeEnviarWhatsapp ? 'is-success' : 'is-light'"
                                                @icon-right-click="abrirWhatsapp"
                                            />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                                <div class="column is-4">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="required|email" name="E-mail">
                                        <b-field label="E-mail *" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input
                                                ref="email"
                                                v-model.trim="model.email"
                                                maxlength="200"
                                                type="email"
                                                :readonly="model.possuiUsuario"
                                                @blur="verificarEmail"
                                            />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                            </div>
                        </article>

                        <article class="form-section-card">
                            <header class="form-section-header">
                                <span class="form-section-icon"><b-icon icon="phone-outline" /></span>
                                <div>
                                    <h2>Contato do Financeiro</h2>
                                    <p>Canais de contato para assuntos financeiros</p>
                                </div>
                            </header>

                            <div class="columns is-multiline">
                                <div class="column is-12">
                                    <b-field label="Nome">
                                        <b-input v-model.trim="model.nomeContatoFinanceiro" maxlength="150" />
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Telefone">
                                        <b-input v-model="model.telefoneFinanceiro" v-mask="['(##) ####-####', '(##) #####-####']" maxlength="50" />
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <b-field label="Celular">
                                        <b-input v-model="model.celularFinanceiro" v-mask="['(##) ####-####', '(##) #####-####']" maxlength="50" />
                                    </b-field>
                                </div>
                                <div class="column is-4">
                                    <ValidationProvider v-slot="{ errors, valid }" rules="email" name="E-mail financeiro">
                                        <b-field label="E-mail" :type="{ 'is-danger': errors[0], 'is-success': valid }" :message="errors">
                                            <b-input v-model.trim="model.emailFinanceiro" maxlength="200" type="email" />
                                        </b-field>
                                    </ValidationProvider>
                                </div>
                            </div>
                        </article>

                        <article class="form-section-card">
                            <header class="form-section-header">
                                <span class="form-section-icon"><b-icon icon="map-marker-outline" /></span>
                                <div>
                                    <h2>Localização e observações</h2>
                                    <p>A cidade selecionada determina automaticamente o estado.</p>
                                </div>
                            </header>

                            <div class="columns is-multiline">
                                <div class="column is-4">
                                    <b-field label="CEP">
                                        <b-input v-model="model.cep" v-mask="'#####-###'" maxlength="9" inputmode="numeric" />
                                    </b-field>
                                </div>
                                <div class="column is-12">
                                    <b-field label="Endereço">
                                        <b-input v-model.trim="model.endereco" maxlength="500" />
                                    </b-field>
                                </div>
                                <div class="column is-8">
                                    <buscaCidade
                                        :cidadeid="model.cidadeId"
                                        :cidadenome="model.cidadeNome"
                                        @selected="atualizarCidade"
                                        @update:cidadeid="model.cidadeId = $event"
                                    />
                                </div>
                                <div class="column is-4">
                                    <b-field label="Estado" message="Preenchido pela cidade">
                                        <b-input :value="model.estado" readonly />
                                    </b-field>
                                </div>
                                <div class="column is-12">
                                    <b-field label="Observações">
                                        <b-input v-model.trim="model.observacoes" type="textarea" maxlength="500" />
                                    </b-field>
                                </div>
                            </div>
                        </article>
                    </div>

                    <div class="column is-3-widescreen is-4-desktop" style="padding-right: 0 !important;">
                        <aside class="cliente-sidebar" :class="{ 'has-modal-active': historicoModalAberto }">
                            <div class="sidebar-card">


                                <template v-if="model.id">
                                    <button class="quick-action" type="button" @click="modalIncluirAnexo">
                                        <span class="quick-action-icon"><b-icon icon="folder-multiple-outline" /></span>
                                        <span><strong>Documentos</strong><small>Anexos e arquivos pessoais</small></span>
                                        <b-icon icon="chevron-right" size="is-small" />
                                    </button>

                                    <historico-alteracoes class="history-entry" tabela="Cliente" :chave="model.id" :panel-header="false">
                                        <template #trigger="{ open }">
                                            <button class="quick-action history-quick-action" type="button" @click="open">
                                                <span class="quick-action-icon"><b-icon icon="history" /></span>
                                                <span><strong>Histórico</strong><small>Alterações realizadas</small></span>
                                                <b-icon icon="chevron-right" size="is-small" />
                                            </button>
                                        </template>
                                    </historico-alteracoes>
                                </template>

                                <div v-else class="sidebar-empty">
                                    <b-icon icon="content-save-outline" size="is-medium" />
                                    <p>Salve o cadastro para adicionar documentos e consultar o histórico.</p>
                                </div>
                            </div>

                        </aside>
                    </div>
                </div>

                <footer class="cliente-form-actions">
                    <b-button type="is-light" native-type="button" icon-left="arrow-left" @click="$router.back()">
                        {{ $t('SISTEMA.CANCELAR') }}
                    </b-button>
                    <b-button
                        v-show="isInRole('prd-clientes-alterar')"
                        type="is-primary"
                        :loading="salvandoDados"
                        native-type="submit"
                        icon-left="content-save-outline"
                    >
                        {{ $t('SISTEMA.SALVAR') }}
                    </b-button>
                </footer>
            </form>
        </ValidationObserver>
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import buscaCidade from '@/components/buscaCidade.vue'
import titlebar from '@/components/titlebar.vue'
import historicoAlteracoes from '@/components/historicoAlteracoes.vue'
import moment from 'moment'
import calcularIdade from '../../assets/js/views/cliente-idade/calcularIdade.js'
import modalAnexosGenerico from '@/components/modalAnexosGenerico.vue'
import modalInserirClienteNovoEtapa from '@/components/posvenda/modalInserirClienteNovoEtapa'
import modalDocumentoVenda from '@/components/venda/modalDocumentoVenda.vue'

export default {
    data() {
        return {
            model: {
                id: null,
                nome: this.$route.params.nome ?? null,
                razaoSocial: null,
                nomeFantasia: null,
                inscricaoMunicipal: null,
                inscricaoEstadual: null,
                fone: null,
                celular: null,
                nomeContatoCliente: null,
                nomeContatoFinanceiro: null,
                telefoneFinanceiro: null,
                celularFinanceiro: null,
                emailFinanceiro: null,
                empresaNome: null,
                sexo: null,
                dataDeNascimento: null,
                cep: null,
                endereco: null,
                bairro: null,
                cidadeId: null,
                cidadeNome: null,
                estado: null,
                rg: null,
                cpf: null,
                email: null,
                observacoes: null,
                criaUsuario: false,
                indicacao: null,
                foto: null,
                unidadeIdade: null,
                isLoading: false,
            },
            dataAtual: new Date(new Date().setHours(0, 0, 0, 0)),
            carregando: true,
            salvandoDados: false,
            erroCpfCnpj: null,
            nomeSocial: false,
            fotoAlterada: false,
            criarGuia: Boolean(this.$route.params.criarGuia) ?? false,
            arquivosCarregados: false,
            agendamentos: null,
            guia: null,
            bloquearCodigo: Boolean(this.$route.params.bloquearCodigo) ?? false,

            opcoesIndicacao: ['Google', 'Facebook', 'Instagram', 'Panfleto', 'Revista', 'Solicitante', 'Amigo', 'Outros'],
        }
    },
    components: {
        buscaCidade,
        titlebar,
        historicoAlteracoes,
    },

    created() {
        this.isLoading = true
        if (this.$route.params.id > 0) {
            let url = '/api/manutencao/cliente?id=' + this.$route.params.id + '&unidadeId=' + this.unidadeId

            if (this.$route.params.id) {
                url = '/api/manutencao/cliente?id=' + this.$route.params.id + '&unidadeId=' + this.unidadeId
            }

            http.get(url)
                .then((res) => {
                    this.model = res.data

                    if (this.model.dataDeNascimento) {
                        this.model.dataDeNascimento = new Date(this.model.dataDeNascimento)
                    }

                    if (this.model?.foto) {
                        if (this.model.foto.indexOf('data:image/') < 0) {
                            this.model.foto = ` data:image/*;charset=utf-8;base64,${this.model.foto}`
                        }
                    }

                    this.nomeSocial = this.model.nomeSocial != null

                    this.model.criaUsuario = this.model.usuario != null

                    this.loadDataAgendamentos()
                    this.carregando = false
                })
                .finally(() => {
                    this.isLoading = false
                })
        } else {
            this.carregando = false
            this.isLoading = false
        }
    },
    computed: {
        ...mapState(['unidadeId']),

        historicoModalAberto() {
            return this.$store.state.componentes.modalHistoricoIsOpened
        },
        titleStack() {
            return [this.$t('SISTEMA.MANUTENCAO'), this.$t('SISTEMA.CLIENTE'), this.model.id ?? this.$t('SISTEMA.NOVO')]
        },
        podeEnviarWhatsapp() {
            const celular = String(this.model.celular || '').replace(/\D/g, '')
            return Boolean(this.model.id && celular.length >= 10)
        },
        idade() {
            if (this.model.dataDeNascimento == null) {
                return ''
            }

            return this.calcularIdade()
        },
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
    },
    methods: {
        preencherRazaoSocial() {
            if (!this.model.razaoSocial?.trim()) {
                this.model.razaoSocial = this.model.nome?.trim() || null
            }
        },
        atualizarCidade(cidade) {
            this.model.cidadeNome = cidade?.nome ?? null
            this.model.estado = cidade?.estado ?? null
        },

        async limparVendaDireta() {
            const motivoCancelamentoGuiaSelecionado = 'Venda Direta Cancelada'

            const params = []
            params.push(`id=${this.guia.id}`)
            params.push(`motivo=${motivoCancelamentoGuiaSelecionado}`)

            try {
                http.get(`/api/atendimento/cancelarGuia?${params.join('&')}`).then(() => {
                    return true
                })
            } catch (e) {
                console.error(e)
            }
        },
        async modalDocumentoVendaDireta() {
            this.salvandoDados = false

            this.$buefy.dialog.confirm({
                message: this.$t('CLIENTECADASTRO.EFETIVARVENDAMENSAGEM'),
                cancelText: 'Não',
                confirmText: 'Sim',
                onConfirm: () => {
                    const novaGuia = {
                        id: 0,
                        clienteId: this.model.id,
                        unidadeDeCadastroId: this.unidadeId,
                        dataHoraCadastro: new Date(),
                        autorizacao: false,
                        prazoProjetoEmDias: 0,
                        prazoVendedorEmDias: 0,
                        numeroAmbiente: 0,
                    }

                    http.post('/api/atendimento/edit', novaGuia)
                        .then((res) => res.data)
                        .then((data) => {
                            http.get('/api/atendimento/edit?id=' + data)
                                .then((res) => {
                                    this.guia = res.data[0]
                                })
                                .finally(() => {
                                    this.$buefy.modal.open({
                                        parent: this,
                                        component: modalDocumentoVenda,
                                        props: {
                                            guia: this.guia,
                                            semAgendamento: true,
                                            vendaDireta: true,
                                        },
                                        events: {
                                            limparVendaDireta: this.limparVendaDireta,
                                        },
                                        hasModalCard: true,
                                        trapFocus: true,
                                        fullScreen: true,
                                        canCancel: false,
                                    })
                                })
                        })
                        .catch((err) => {
                            this.salvandoDados = false
                            // restrições guia
                            if (err.status == 404) {
                                this.$buefy.toast.open({
                                    duration: 10000,
                                    message: err.body,
                                    type: 'is-danger',
                                    queue: false,
                                })
                            } else {
                                console.log('ERRO:' + err.body)

                                this.$buefy.toast.open({
                                    duration: 10000,
                                    message: err.body,
                                    type: 'is-danger',
                                    queue: false,
                                })
                            }
                        })
                },
                onCancel: () => {
                    return false
                },
            })
        },
        recarregarposvendasInstalacaoFutura() {
            this.$router.push({ name: 'posvenda' })
        },
        inserirClienteNovoEtapa(tipo) {
            this.$buefy.modal.open({
                parent: this,
                component: modalInserirClienteNovoEtapa,
                props: {
                    tipoEtapa: tipo,
                },
                events: {
                    recarregarposvendasInstalacaoFutura: this.recarregarposvendasInstalacaoFutura,
                },
                hasModalCard: true,
                trapFocus: true,
            })
        },

        listarAtendimentos() {
            this.$router.push({ name: 'guia-list', query: { clienteId: this.model.id } })
        },
        modalIncluirAnexo() {
            this.$buefy.modal.open({
                parent: this,
                component: modalAnexosGenerico,
                props: {
                    id: this.model.id,
                    titleName: 'Anexos',
                    controller: {
                        mainController: 'manutencao',
                        methodAnexoController: 'anexoCliente',
                        methodRemoverController: 'excluirAnexo',
                    },
                    aliasName: 'Cliente',
                    tipoParam: {
                        firstType: 'id',
                        secondType: 'cliente',
                    },
                },
                hasModalCard: true,
                trapFocus: true,
            })
        },
        tipoAgendamentoStyle(evento) {
            return 'color: #' + evento.tipoAgendamentoCor + '!important'
        },
        loadDataAgendamentos() {
            const params = []

            if (this.dataAtual != null) params.push(`dataInicial=${moment(this.dataAtual).format('YYYY-MM-DDTHH:mm:ss')}`)
            if (this.model.id) params.push(`clienteId=${this.model.id}`)

            params.push(`unidadeId=${this.unidadeId}`)

            try {
                http.get(`/api/agenda/RelacaoCliente?${params.join('&')}`)
                    .then((res) => res.data)
                    .then((data) => {
                        if (data != null) {
                            this.agendamentos = data?.map((d) => {
                                return {
                                    id: d.id,
                                    clienteId: d.clienteId,
                                    tipoAgendamentoId: d.tipoAgendamentoId,
                                    tipoAgendamentoCor: d.tipoAgendamentoCor,
                                    tipoAgendamentoNome: d.tipoAgendamentoNome,
                                    dataHora: moment(d.dataHora).format('DD/MM/YYYY HH:mm:ss'),
                                }
                            })
                        }
                    })
                    .catch((e) => {
                        throw e
                    })
                    .finally(() => {
                        this.$forceUpdate()
                        this.carregando = false
                    })
            } catch (e) {
                console.error(e)
                this.carregando = false
                throw e
            }
        },
        abrirVerMais() {
            const params = {}
            params.clienteNome = this.model.nome

            this.$router.push({
                name: 'agenda-relacao',
                params: params,
            })
        },
        calcularIdade() {
            return calcularIdade.Idade(this.model.dataDeNascimento)
        },

        alterouIdade(event) {
            const id = event.target.value.replace(',', '.')

            if (id < 1) {
                this.$buefy.toast.open({
                    duration: 5000,
                    message: this.$t('CLIENTECADASTRO.IDADEINVALIDA'),
                    type: 'is-danger',
                    queue: false,
                })

                return
            }

            if (this.model.unidadeIdade == 'D') {
                const dataNascimento = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
                const quantidade = id * 24 * 60 * 60 * 1000
                this.model.dataDeNascimento = new Date(dataNascimento.valueOf() - quantidade)
            } else if (this.model.unidadeIdade == 'M') {
                const dataNascimento = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
                const quantidade = id * 31 * 24 * 60 * 60 * 1000
                this.model.dataDeNascimento = new Date(dataNascimento.valueOf() - quantidade)
            } else {
                this.model.dataDeNascimento = new Date(new Date().getFullYear() - id, new Date().getMonth(), new Date().getDate() - 1)
            }
        },

        verificarEmail() {
            http.get(`/api/manutencao/VerificarUsuarioEmail?email=${this.model.email}`).then((res) => {
                if (res.data?.existe && !(res.data.tipo == 'cliente' && res.data.id == this.model.id)) {
                    this.$buefy.toast.open({
                        duration: 5000,
                        message:
                            this.$t('CLIENTECADASTRO.EMAILMSG') + res.data.email + this.$t('CLIENTECADASTRO.EMUSO') + res.data.tipo + ' código ' + res.data.id,
                        type: 'is-danger',
                        queue: false,
                    })
                    this.model.email = null
                }
            })
        },


        async onSubmit() {
            this.salvandoDados = true

            if (!this.nomeSocial && this.model.nomeSocial != null) {
                this.model.nomeSocial = null
            }

            this.model.UnidadeDeCadastro = { id: this.unidadeId }

            if (this.model.criaUsuario && this.model.email == null) {
                this.$buefy.toast.open({
                    duration: 5000,
                    message: this.$t('CLIENTECADASTRO.FALHAUSUARIO'),
                    type: 'is-danger',
                    queue: false,
                })
                this.$refs.email.focus()
                this.salvandoDados = false
                return
            }

            this.salvarCliente()
            
        },
        salvarCliente() {
            this.erroCpfCnpj = null
            this.model.emailFinanceiro = this.model.emailFinanceiro?.trim() || null
            this.model.nome = this.model.nome.toUpperCase()
            http.post('/api/manutencao/cliente', this.model)
                .then((res) => {
                    if (res.data.mensagem != null) {
                        this.$buefy.toast.open({
                            duration: 5000,
                            message: res.data.mensagem,
                            type: 'is-success',
                            queue: false,
                        })
                    }

                    if (this.criarGuia && !this.model.desativado) {
                        this.$router.push({ name: 'novaguia', params: { clienteId: res.data.id } })
                    } else {
                        this.$router.back()
                    }
                })
                .catch((err) => {
                    this.salvandoDados = false
                    const body = err.body || err.data || err.response?.data || {}
                    const errors = body.errors || {}
                    const mensagens = Object.keys(errors).reduce((lista, campo) => {
                        const mensagensCampo = Array.isArray(errors[campo]) ? errors[campo] : [errors[campo]]
                        mensagensCampo.filter(Boolean).forEach((mensagem) => {
                            lista.push(mensagem)
                            this.$store.commit('ADD_ERRO', `${campo}: ${mensagem}`)
                        })
                        return lista
                    }, [])

                    const errosDocumento = Array.isArray(errors.CPF) ? errors.CPF : errors.CPF ? [errors.CPF] : []
                    this.erroCpfCnpj = errosDocumento.filter(Boolean).join(' ')

                    this.$buefy.toast.open({
                        duration: 7000,
                        message: mensagens.join(' ') || body.erro || body.title || this.$t('CLIENTECADASTRO.ERROCLIENTE'),
                        type: 'is-danger',
                        queue: false,
                    })
                })
        },

        abrirWhatsapp() {
            if (!this.podeEnviarWhatsapp) return

            const celular = String(this.model.celular).replace(/\D/g, '')
            const numeroWhatsapp = celular.length <= 11 ? `55${celular}` : celular
            const mensagem = `Olá, ${this.model.nome}! Aqui é da ${this.model.empresaNome}. Estamos entrando em contato com você. Tudo bem?`
            const url = `https://wa.me/${numeroWhatsapp}?text=${encodeURIComponent(mensagem)}`

            window.open(url, '_blank', 'noopener,noreferrer')
        },
    },
}
</script>

<style scoped>
.cliente-page {
    min-height: 100%;
    overflow: hidden;
    background: var(--surface-page);
}
.cliente-form {
    max-width: 1380px;
    margin: 0 auto;
    padding: 1rem 1.25rem 1.5rem;
}
.cliente-context-card {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin-bottom: 1rem;
    padding: 1rem 1.15rem;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 5px 18px rgba(31, 53, 78, 0.04);
}
.cliente-context-icon,
.form-section-icon,
.quick-action-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: var(--brand-navy-700);
    background: var(--brand-teal-100);
}
.cliente-context-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
}
.cliente-context-content {
    min-width: 0;
    flex: 1;
}
.cliente-context-content h1 {
    margin: 0.1rem 0;
    overflow: hidden;
    color: #2f4052;
    font-size: 1.15rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cliente-context-content p,
.form-section-header p {
    color: #7d8b99;
    font-size: 0.78rem;
}
.cliente-eyebrow {
    color: var(--brand-teal-600);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
}
.required-legend {
    align-self: flex-start;
    color: #7d8b99;
    font-size: 0.72rem;
    white-space: nowrap;
}
.required-legend span {
    color: #d64545;
    font-weight: 700;
}
.cliente-layout {
    margin-bottom: 0 !important;
}
.form-section-card {
    margin-bottom: 1rem;
    padding: 1.2rem 1.25rem 0.35rem;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 5px 18px rgba(31, 53, 78, 0.035);
}
.form-section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 0.85rem;
    border-bottom: 1px solid #edf1f5;
}
.form-section-header h2 {
    margin: 0 0 0.12rem;
    color: #34495e;
    font-size: 1rem;
    font-weight: 700;
}
.form-section-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
}
.cliente-sidebar {
    position: sticky;
    top: 1rem;
}
.cliente-sidebar.has-modal-active {
    z-index: 41;
}
.sidebar-card {
    overflow: hidden;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 5px 18px rgba(31, 53, 78, 0.035);
}
.sidebar-card-header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem;
    border-bottom: 1px solid #edf1f5;
    color: var(--brand-teal-600);
}
.sidebar-card-header > div {
    display: flex;
    flex-direction: column;
}
.sidebar-card-header strong {
    color: #34495e;
}
.sidebar-card-header small,
.quick-action small {
    color: #8492a0;
    font-size: 0.7rem;
}
.quick-action {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.8rem 1rem;
    border: 0;
    border-bottom: 1px solid #f0f3f6;
    background: #fff;
    color: #415365;
    text-align: left;
}
.quick-action {
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}
.quick-action:hover,
.quick-action:focus-visible {
    background: #f0f8f8;
    color: var(--brand-navy-700);
    outline: none;
}
.quick-action > span:nth-child(2) {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
}
.quick-action-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
}
.history-entry {
    margin: 0;
    border: 0;
    box-shadow: none;
}
.history-entry ::v-deep .panel-block {
    padding: 0;
    border: 0;
}
.history-quick-action {
    border-bottom: 0;
}
.sidebar-empty {
    padding: 1.5rem 1rem;
    color: #7d8b99;
    text-align: center;
}
.sidebar-empty p {
    margin-top: 0.65rem;
    font-size: 0.78rem;
    line-height: 1.45;
}
.sidebar-help {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.8rem 0.9rem;
    border-radius: 10px;
    background: #eef8f3;
    color: #3d6f58;
    font-size: 0.74rem;
}
.cliente-form-actions {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    margin-top: 0.25rem;
    padding: 0.85rem 1rem;
    border: 1px solid var(--border-soft);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 -5px 20px rgba(31, 53, 78, 0.06);
    backdrop-filter: blur(8px);
}
@media (max-width: 1023px) {
    .cliente-sidebar {
        position: static;
    }
}
@media (max-width: 768px) {
    .cliente-form {
        padding: 0.75rem;
    }
    .cliente-context-card {
        align-items: flex-start;
    }
    .cliente-context-icon {
        width: 42px;
        height: 42px;
    }
    .required-legend {
        display: none;
    }
    .cliente-context-content h1 {
        white-space: normal;
    }
    .form-section-card {
        padding: 1rem 1rem 0.2rem;
    }
    .cliente-form-actions {
        padding: 0.75rem;
    }
    .cliente-form-actions .button {
        flex: 1;
    }
}
</style>
